'use client';

import { useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { getCountries } from '@/lib/data-source';
import { getSlugForCountry, getAvailableLocales, uiFb } from '@/lib/i18n';
import { setLangOverride } from '@/hooks/useAutoLang';
import { detectGeoCountry, detectBrowserLang } from '@/hooks/useAutoCountry';
import { Flag } from '@/components/ui/Flag';
import { CountryCombobox, type ComboboxCountry } from '@/components/ui/CountryCombobox';
import { MadLibsSlot } from './MadLibsSlot';
import {
  getAvailableInterests,
  getCountriesForInterest,
  getTargetUrl,
  inferNavStateFromPath,
  type InterestType,
} from '@/lib/nav-availability';
import { ORIGIN_KEY } from '@/lib/user-preferences';
import type { CountryCode } from '@/lib/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INTEREST_KEYS: InterestType[] = ['holidays', 'festivals', 'longweekends', 'besttime', 'localevents'];

const INTEREST_FALLBACKS: Record<string, string> = {
  holidays: 'public holidays',
  festivals: 'festivals',
  longweekends: 'long weekends',
  besttime: 'best time to visit',
  localevents: 'local events',
};

type SlotId = 'origin' | 'language' | 'interest' | 'destination';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface LangAlternate {
  lang: string;
  slug: string;
  label: string;
  labelEn?: string;
}

function getStoredOrigin(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ORIGIN_KEY);
}

function setStoredOrigin(code: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ORIGIN_KEY, code);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MadLibsNavProps {
  lang: string;
  langAlternates?: LangAlternate[];
  activeCountryCode?: string;
  mobile?: boolean;
}

export function MadLibsNav({ lang, langAlternates = [], activeCountryCode, mobile }: MadLibsNavProps) {
  const allCountries = useMemo(() => getCountries(), []);
  const displayedCountries = useMemo(() => allCountries.filter(c => c.isDisplayed), [allCountries]);
  const locales = useMemo(() => getAvailableLocales(), []);

  // Infer initial state
  const [origin, setOrigin] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>(lang);
  const [interest, setInterest] = useState<InterestType | null>(null);
  const [destination, setDestination] = useState<string | null>(null);

  // Open slot tracking — only one at a time
  const [openSlot, setOpenSlot] = useState<SlotId | null>(null);

  // Hydrate from localStorage + URL on mount
  useEffect(() => {
    // Restore saved origin country
    const stored = getStoredOrigin();
    if (stored) setOrigin(stored);

    // Detect browser language and auto-select it
    const browserLang = detectBrowserLang();
    if (locales.some(l => l.code === browserLang)) {
      setLanguage(browserLang);
    }

    // Infer interest + destination from current URL
    const inferred = inferNavStateFromPath(window.location.pathname);
    if (inferred.interest) setInterest(inferred.interest);
    if (inferred.country) setDestination(inferred.country);

    // activeCountryCode always wins for destination (page knows its country)
    if (activeCountryCode) {
      setDestination(activeCountryCode);
      if (!inferred.interest) setInterest('holidays');
    }
  }, [activeCountryCode, locales]);

  // Auto-detect origin country via IP geolocation (non-blocking)
  useEffect(() => {
    if (origin) return; // user already has a stored origin

    const controller = new AbortController();
    detectGeoCountry(controller.signal).then(code => {
      if (controller.signal.aborted) return;
      if (code && allCountries.some(c => c.code === code)) {
        setOrigin(code);
        setStoredOrigin(code);
      }
    });

    return () => controller.abort();
  }, [origin, allCountries]);

  const toggleSlot = useCallback((id: SlotId) => {
    setOpenSlot(prev => prev === id ? null : id);
  }, []);

  const closeSlot = useCallback(() => {
    setOpenSlot(null);
  }, []);

  // --- Origin options (all world countries — user could be from anywhere) ---
  const originCountries: ComboboxCountry[] = useMemo(() =>
    allCountries.map(c => ({ code: c.code, name: c.name, region: c.region })),
  [allCountries]);

  // --- Language options ---
  const languageOptions = useMemo(() =>
    locales.map(l => ({ id: l.code, label: l.label })),
  [locales]);

  // --- Interest options (filtered by destination) ---
  const interestOptions = useMemo(() => {
    const available = destination
      ? getAvailableInterests(destination as CountryCode)
      : new Set<InterestType>(INTEREST_KEYS);

    return INTEREST_KEYS
      .filter(k => available.has(k))
      .map(k => ({ id: k, label: uiFb(`nav.sentence.interest.${k}`, INTEREST_FALLBACKS[k] ?? k, lang) }));
  }, [destination, lang]);

  // --- Destination options (displayed countries, filtered by interest) ---
  const destinationCountries: ComboboxCountry[] = useMemo(() => {
    const allowed = interest ? getCountriesForInterest(interest) : new Set<CountryCode>();
    const showAll = !interest || allowed.size === 0; // holidays returns empty = all

    return displayedCountries
      .filter(c => showAll || allowed.has(c.code as CountryCode))
      .map(c => ({ code: c.code, name: c.name, region: c.region }));
  }, [displayedCountries, interest]);

  // --- Handlers ---
  const handleOriginSelect = useCallback((id: string) => {
    setOrigin(id);
    setStoredOrigin(id);
  }, []);

  const handleLanguageSelect = useCallback((id: string) => {
    setLanguage(id);
    setLangOverride(id);
    // Find matching alternate slug
    const alt = langAlternates.find(a => a.lang === id);
    if (alt) {
      window.location.href = '/' + alt.slug;
    }
  }, [langAlternates]);

  const handleInterestSelect = useCallback((id: string) => {
    const newInterest = id as InterestType;
    setInterest(newInterest);

    // Clear destination if it's not available for new interest
    if (destination) {
      const allowed = getCountriesForInterest(newInterest);
      if (allowed.size > 0 && !allowed.has(destination as CountryCode)) {
        setDestination(null);
        return;
      }
    }

    // Navigate if both are set
    if (destination) {
      navigate(newInterest, destination as CountryCode);
    }
  }, [destination, lang]);

  const handleDestinationSelect = useCallback((id: string) => {
    setDestination(id);

    // Clear interest if it's not available for new destination
    if (interest) {
      const available = getAvailableInterests(id as CountryCode);
      if (!available.has(interest)) {
        setInterest(null);
        return;
      }
    }

    // Navigate if both are set
    const activeInterest = interest || 'holidays';
    navigate(activeInterest, id as CountryCode);
  }, [interest, lang]);

  function navigate(int: InterestType, dest: CountryCode) {
    const url = getTargetUrl(int, dest, lang);
    if (url) {
      window.location.href = '/' + url;
    }
  }

  // --- Slot renderers (shared between desktop and mobile) ---

  const slotMap: Record<string, ReactNode> = {
    origin: (
      <CountryCombobox
        countries={originCountries}
        value={origin}
        onChange={handleOriginSelect}
        variant="inline"
        mode="select"
        label={uiFb('nav.sentence.origin_placeholder', 'anywhere', lang)}
      />
    ),
    language: (
      <MadLibsSlot
        value={language}
        placeholder={uiFb('nav.sentence.language_placeholder', 'any language', lang)}
        options={languageOptions}
        isOpen={openSlot === 'language'}
        onToggle={() => toggleSlot('language')}
        onClose={closeSlot}
        onSelect={handleLanguageSelect}
        mobile={mobile}
      />
    ),
    interest: (
      <MadLibsSlot
        value={interest}
        placeholder={uiFb('nav.sentence.interest_placeholder', 'everything', lang)}
        options={interestOptions}
        isOpen={openSlot === 'interest'}
        onToggle={() => toggleSlot('interest')}
        onClose={closeSlot}
        onSelect={handleInterestSelect}
        mobile={mobile}
      />
    ),
    destination: (
      <CountryCombobox
        countries={destinationCountries}
        value={destination}
        onChange={handleDestinationSelect}
        variant="inline"
        mode="select"
        label={uiFb('nav.sentence.destination_placeholder', 'everywhere', lang)}
      />
    ),
  };

  // --- Template parsing ---
  const TEMPLATE_FALLBACKS: Record<string, string> = {
    'nav.sentence.primary': 'Explore {slot:interest} in {slot:destination}',
    'nav.sentence.secondary': 'from {slot:origin}, in {slot:language}',
  };

  function renderTemplate(templateKey: string): ReactNode[] {
    const template = uiFb(templateKey, TEMPLATE_FALLBACKS[templateKey] ?? templateKey, lang);
    const parts = template.split(/(\{slot:\w+\})/);
    return parts.map((part, i) => {
      const match = part.match(/^\{slot:(\w+)\}$/);
      if (!match) return <span key={i}>{part}</span>;
      const slotName = match[1];
      return <span key={i}>{slotMap[slotName] ?? part}</span>;
    });
  }

  // Check if secondary slots are all defaults (no user selection)
  const showSecondary = origin !== null || language !== lang;

  if (mobile) {
    return (
      <div className="flex flex-col gap-3" role="search" aria-label="Navigate by interest and country">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-text-dim">{uiFb('nav.sentence.interest_placeholder', 'everything', lang)}</span>
          <MadLibsSlot
            value={interest}
            placeholder={uiFb('nav.sentence.interest_placeholder', 'everything', lang)}
            options={interestOptions}
            isOpen={openSlot === 'interest'}
            onToggle={() => toggleSlot('interest')}
            onClose={closeSlot}
            onSelect={handleInterestSelect}
            mobile
          />
          <CountryCombobox
            countries={destinationCountries}
            value={destination}
            onChange={handleDestinationSelect}
            variant="button"
            mode="select"
            label={uiFb('nav.sentence.destination_placeholder', 'everywhere', lang)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-text-dim">{uiFb('nav.sentence.origin_placeholder', 'anywhere', lang)}</span>
          <CountryCombobox
            countries={originCountries}
            value={origin}
            onChange={handleOriginSelect}
            variant="button"
            mode="select"
            label={uiFb('nav.sentence.origin_placeholder', 'anywhere', lang)}
          />
          <MadLibsSlot
            value={language}
            placeholder={uiFb('nav.sentence.language_placeholder', 'any language', lang)}
            options={languageOptions}
            isOpen={openSlot === 'language'}
            onToggle={() => toggleSlot('language')}
            onClose={closeSlot}
            onSelect={handleLanguageSelect}
            mobile
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center text-sm" role="search" aria-label="Navigate by interest and country">
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        {renderTemplate('nav.sentence.primary')}
        {showSecondary && (
          <>
            <span className="text-text-dim">{uiFb('nav.sentence.separator', ' — ', lang)}</span>
            {renderTemplate('nav.sentence.secondary')}
          </>
        )}
      </span>
    </div>
  );
}
